/**
 * Portfolio Backtester (migrada do Streamlit; equivalência em
 * backend/tests_paginas/test_backtester.py).
 *
 * A configuração inteira (período, taxa, benchmarks e a tabela de carteiras) fica na URL
 * (`cfg`, JSON). Como no legado, o backtest só roda no clique em "Rodar backtest"; a
 * tabela é um rascunho até lá.
 */
import {
  ActionIcon,
  Alert,
  Button,
  Checkbox,
  Group,
  Loader,
  MultiSelect,
  NumberInput,
  Select,
  Stack,
  Table,
  Text,
} from '@mantine/core';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { ErroApi, apiGet, apiPost, type Envelope } from '../../api/cliente';
import { AvisosApi } from '../../components/AvisosApi';
import { BarraFiltros, CampoData, GrupoFiltros, LARGURA } from '../../components/Filtros';
import { CabecalhoPagina, Pagina, Secao } from '../../components/Layout';
import { TabelaDados, type TabelaApi } from '../../components/TabelaDados';
import { Tearsheet, type BlocoMovel, type TearsheetApi } from '../../components/Tearsheet';
import { useFiltrosUrl } from '../../lib/url-state';

const CAMINHO = '/analise-quantitativa/portfolio-backtester';
const FX_NONE = '—';

interface Linha {
  ticker: string;
  fx: string;
  pesos: (number | null)[];
}
export interface ConfigBacktest {
  inicio: string;
  fim: string;
  rf: number;
  benchmarks: string[];
  portfolios: number;
  linhas: Linha[];
}
export const CONFIG_PADRAO: ConfigBacktest = {
  inicio: '2010-01-01',
  fim: '',
  rf: 10.75,
  benchmarks: [],
  portfolios: 1,
  linhas: [],
};

export function lerConfig(texto: string): ConfigBacktest | null {
  if (!texto) return null;
  try {
    return { ...CONFIG_PADRAO, ...(JSON.parse(texto) as Partial<ConfigBacktest>) };
  } catch {
    return null;
  }
}

interface Catalogo {
  ativos: { codigo: string; rotulo: string }[];
  benchmarks: string[];
  fx: string[];
  max_ativos: number;
  max_portfolios: number;
}
interface Resultado {
  sem_resultado?: string;
  avisos: string[];
  cobertura?: { tabela: TabelaApi; limitante: string | null; data: string | null };
  tearsheet?: TearsheetApi;
}

function mensagem(erro: unknown, padrao: string) {
  return erro instanceof ErroApi ? erro.message : padrao;
}

const hoje = () => new Date().toISOString().slice(0, 10);

/** Soma dos pesos de uma carteira (vazio conta como zero). */
export function somaPesos(linhas: Linha[], i: number) {
  return linhas.reduce((s, l) => s + (l.pesos[i] ?? 0), 0);
}

function Editor({
  inicial,
  catalogo,
  carregando,
  aoRodar,
}: {
  inicial: ConfigBacktest;
  catalogo: Catalogo | undefined;
  carregando: boolean;
  aoRodar: (c: ConfigBacktest) => void;
}) {
  const [c, setC] = useState(inicial);
  const [novos, setNovos] = useState<string[]>([]);
  const mudar = (m: Partial<ConfigBacktest>) => setC((a) => ({ ...a, ...m }));
  const rotulos = useMemo(
    () => new Map((catalogo?.ativos ?? []).map((a) => [a.codigo, a.rotulo])),
    [catalogo],
  );
  const opcoes = useMemo(
    () => (catalogo?.ativos ?? []).map((a) => ({ value: a.codigo, label: a.rotulo })),
    [catalogo],
  );
  const maxAtivos = catalogo?.max_ativos ?? 50;
  const maxPortfolios = catalogo?.max_portfolios ?? 10;
  const fim = c.fim || hoje();
  const datasInvalidas = fim < c.inicio;

  const adicionar = () => {
    const existentes = new Set(c.linhas.map((l) => l.ticker));
    const extras = novos
      .filter((t) => !existentes.has(t))
      .map((ticker) => ({ ticker, fx: FX_NONE, pesos: Array(c.portfolios).fill(0) }));
    mudar({ linhas: [...c.linhas, ...extras].slice(0, maxAtivos) });
    setNovos([]);
  };
  const mudarLinha = (i: number, m: Partial<Linha>) =>
    mudar({ linhas: c.linhas.map((l, j) => (j === i ? { ...l, ...m } : l)) });
  const mudarPeso = (i: number, p: number, v: number | null) =>
    mudarLinha(i, { pesos: c.linhas[i]!.pesos.map((x, k) => (k === p ? v : x)) });
  const portfolios = Array.from({ length: c.portfolios }, (_, i) => i);

  return (
    <>
      <BarraFiltros carregando={carregando}>
        <GrupoFiltros titulo="Período">
          <CampoData
            rotulo="Início"
            valor={c.inicio}
            min="2000-01-01"
            aoMudar={(d) => mudar({ inicio: d })}
          />
          <CampoData
            rotulo="Fim"
            valor={fim}
            min="2000-01-01"
            aoMudar={(d) => mudar({ fim: d === hoje() ? '' : d })}
          />
        </GrupoFiltros>
        <GrupoFiltros titulo="Comparação">
          <NumberInput
            label="Taxa livre de risco (% a.a.)"
            value={c.rf}
            onChange={(v) => mudar({ rf: v === '' ? 0 : Number(v) })}
            min={0}
            max={50}
            step={0.25}
            decimalScale={2}
            decimalSeparator=","
            w={LARGURA.selecao - 40}
          />
          <MultiSelect
            label="Benchmarks"
            data={catalogo?.benchmarks ?? []}
            value={c.benchmarks}
            onChange={(benchmarks) => mudar({ benchmarks })}
            placeholder={c.benchmarks.length ? '' : 'Nenhum'}
            w={LARGURA.selecao + 40}
          />
        </GrupoFiltros>
      </BarraFiltros>

      <Secao
        titulo="Carteiras"
        legenda="Pesos em %. Pesos positivos são normalizados para somar 100%; com algum peso negativo (posição vendida), os valores são a alocação absoluta (ex.: −50, −50, +200). Em Ajuste FX, o preço do ativo é multiplicado pela série cambial."
        acoes={
          <Group gap="xs">
            <Button
              variant="default"
              disabled={c.portfolios <= 1}
              onClick={() =>
                mudar({
                  portfolios: c.portfolios - 1,
                  linhas: c.linhas.map((l) => ({
                    ...l,
                    pesos: l.pesos.slice(0, c.portfolios - 1),
                  })),
                })
              }
            >
              − Portfólio
            </Button>
            <Button
              variant="light"
              disabled={c.portfolios >= maxPortfolios}
              onClick={() =>
                mudar({
                  portfolios: c.portfolios + 1,
                  linhas: c.linhas.map((l) => ({ ...l, pesos: [...l.pesos, 0] })),
                })
              }
            >
              + Portfólio
            </Button>
          </Group>
        }
      >
        <Stack gap="sm">
          <Group align="flex-end" gap="sm">
            <MultiSelect
              label="Adicionar ativos"
              placeholder="Digite para buscar série ou fundo"
              data={opcoes}
              value={novos}
              onChange={setNovos}
              searchable
              clearable
              limit={100}
              w={520}
            />
            <Button variant="light" disabled={!novos.length} onClick={adicionar}>
              Adicionar
            </Button>
          </Group>
          {c.linhas.length === 0 ? (
            <Text size="sm" c="dimmed">
              Nenhum ativo na carteira.
            </Text>
          ) : (
            <Table.ScrollContainer minWidth={600}>
              <Table striped withTableBorder verticalSpacing={4}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Ativo</Table.Th>
                    <Table.Th w={150}>Ajuste FX</Table.Th>
                    {portfolios.map((p) => (
                      <Table.Th key={p} w={130} ta="right">
                        Portfolio {p + 1} (%)
                      </Table.Th>
                    ))}
                    <Table.Th w={40} />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {c.linhas.map((l, i) => (
                    <Table.Tr key={l.ticker}>
                      <Table.Td>
                        <Text size="sm">{rotulos.get(l.ticker) ?? l.ticker}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Select
                          aria-label={`Ajuste FX de ${l.ticker}`}
                          data={catalogo?.fx ?? [FX_NONE]}
                          value={l.fx}
                          onChange={(fx) => fx && mudarLinha(i, { fx })}
                          size="xs"
                        />
                      </Table.Td>
                      {portfolios.map((p) => (
                        <Table.Td key={p}>
                          <NumberInput
                            aria-label={`Peso de ${l.ticker} no Portfolio ${p + 1}`}
                            value={l.pesos[p] ?? ''}
                            onChange={(v) => mudarPeso(i, p, v === '' ? null : Number(v))}
                            decimalScale={1}
                            decimalSeparator=","
                            size="xs"
                            hideControls
                          />
                        </Table.Td>
                      ))}
                      <Table.Td>
                        <ActionIcon
                          variant="subtle"
                          color="gray"
                          aria-label={`Remover ${l.ticker}`}
                          onClick={() => mudar({ linhas: c.linhas.filter((_, j) => j !== i) })}
                        >
                          ✕
                        </ActionIcon>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
                <Table.Tfoot>
                  <Table.Tr>
                    <Table.Th colSpan={2}>Soma</Table.Th>
                    {portfolios.map((p) => (
                      <Table.Th key={p} ta="right">
                        {somaPesos(c.linhas, p).toLocaleString('pt-BR', {
                          maximumFractionDigits: 1,
                        })}
                      </Table.Th>
                    ))}
                    <Table.Th />
                  </Table.Tr>
                </Table.Tfoot>
              </Table>
            </Table.ScrollContainer>
          )}
          <Group justify="flex-end">
            {datasInvalidas && (
              <Text size="sm" c="red">
                A data final deve ser maior ou igual à data inicial.
              </Text>
            )}
            <Button
              color="dourado"
              disabled={!c.linhas.length || datasInvalidas}
              onClick={() => aoRodar(c)}
            >
              Rodar backtest
            </Button>
          </Group>
        </Stack>
      </Secao>
    </>
  );
}

export function PortfolioBacktester() {
  const [filtros, definir] = useFiltrosUrl({ cfg: '' });
  const config = lerConfig(filtros.cfg);
  const [mostrarHistorico, setMostrarHistorico] = useState(false);

  const catalogo = useQuery({
    queryKey: ['quant.backtester.catalogo'],
    queryFn: ({ signal }) => apiGet<Envelope<Catalogo>>(`${CAMINHO}/catalogo`, {}, signal),
    staleTime: 60 * 60 * 1000,
  });
  const corpo = config ? { ...config, fim: config.fim || null } : null;
  const resultado = useQuery({
    queryKey: ['quant.backtester', corpo],
    queryFn: ({ signal }) => apiPost<Envelope<Resultado>>(CAMINHO, corpo, signal),
    enabled: !!config && config.linhas.length > 0,
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
  });
  const dados = resultado.data?.data;
  const historico = useQuery({
    queryKey: ['quant.backtester.historico', corpo],
    queryFn: ({ signal }) =>
      apiPost<Envelope<{ tabela: TabelaApi }>>(`${CAMINHO}/historico`, corpo, signal),
    enabled: mostrarHistorico && !!dados?.tearsheet,
    staleTime: 5 * 60 * 1000,
  });
  const buscarMovel = async (janela: string, sinal: AbortSignal) =>
    (await apiPost<Envelope<BlocoMovel>>(`${CAMINHO}/movel`, { cfg: corpo, janela }, sinal)).data;

  return (
    <Pagina>
      <CabecalhoPagina
        titulo="Portfolio Backtester"
        descricao="Backtest de carteiras de séries e fundos com pesos fixos, ajuste cambial e benchmarks, com o tearsheet de performance."
        dadosAte={resultado.data?.meta.dados_ate}
        fonte="Banco Persevera (indicadores e fundos_cvm) · Fibery (catálogo de fundos)"
      />
      <Editor
        key={filtros.cfg}
        inicial={config ?? CONFIG_PADRAO}
        catalogo={catalogo.data?.data}
        carregando={catalogo.isFetching || resultado.isFetching}
        aoRodar={(c) => definir({ cfg: JSON.stringify(c) })}
      />
      <AvisosApi avisos={[catalogo.data?.meta.avisos, resultado.data?.meta.avisos]} />
      {catalogo.isError && (
        <Alert color="red">
          {mensagem(catalogo.error, 'Não foi possível carregar o catálogo.')}
        </Alert>
      )}
      {!config && (
        <Alert color="blue">
          Adicione séries ou fundos à carteira, defina os pesos e clique em <b>Rodar backtest</b>.
        </Alert>
      )}
      {resultado.isError && (
        <Alert color="red">{mensagem(resultado.error, 'Não foi possível rodar o backtest.')}</Alert>
      )}
      {config && resultado.isPending && <Loader />}
      {dados && !resultado.isError && (
        <>
          {dados.sem_resultado && <Alert color="yellow">{dados.sem_resultado}</Alert>}
          <AvisosApi avisos={[dados.avisos]} />
          {dados.cobertura && dados.cobertura.tabela.linhas.length > 0 && (
            <Secao
              titulo="Cobertura de dados"
              legenda={
                dados.cobertura.limitante && dados.cobertura.data
                  ? `Ativo limitante: ${dados.cobertura.limitante}, histórico a partir de ${dados.cobertura.data}. Antes dessa data, o backtest long-only redistribui os pesos entre os ativos já disponíveis.`
                  : 'Primeira e última data com dado de cada ativo.'
              }
            >
              <TabelaDados tabela={dados.cobertura.tabela} alturaAutomatica />
            </Secao>
          )}
          {dados.tearsheet && (
            <Tearsheet
              key={resultado.dataUpdatedAt}
              dados={dados.tearsheet}
              chave={corpo}
              buscarMovel={buscarMovel}
            />
          )}
          {dados.tearsheet && (
            <Secao
              titulo="Série histórica"
              legenda="Índice dos portfólios e benchmarks (base 100 no início de cada série)."
              acoes={
                <Checkbox
                  label="Mostrar"
                  checked={mostrarHistorico}
                  onChange={(e) => setMostrarHistorico(e.currentTarget.checked)}
                />
              }
            >
              {mostrarHistorico && historico.isFetching && <Loader size="sm" />}
              {mostrarHistorico && historico.isError && (
                <Alert color="red">
                  {mensagem(historico.error, 'Não foi possível carregar a série histórica.')}
                </Alert>
              )}
              {mostrarHistorico && historico.data && (
                <TabelaDados tabela={historico.data.data.tabela} altura={400} />
              )}
            </Secao>
          )}
        </>
      )}
    </Pagina>
  );
}
